import React, { useState, useEffect, useRef } from "react";
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import CommonTable from '../../components/commonComponent/CommanTable';
import { FaSearch, FaPlus, FaEye, FaFilter, FaRegCommentDots, FaTrashAlt, FaEllipsisV } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import CommanFilters from '../../components/commonComponent/CommanFilters';
import axios from '../../utils/axiosInstance';
import { hasPermission } from '../../utils/permissionHelper';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

const leadsVisitsColumns = [
  { key: "srNo", label: "Sr.no" },
  { key: "leadName", label: "Lead Name" },
  { key: "userName", label: "User Name" },
  { key: "note", label: "Note" },
  { key: "createdAt", label: "Visit Date" },
  { key: "action", label: "Action" },
];

const VisitIndex = () => {
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkRow, setRemarkRow] = useState(null);
  const [remarkFormValues, setRemarkFormValues] = useState({ remark: "", image: null });
  const [existingImages, setExistingImages] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ startDate: "", endDate: "" });
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [addVisitForm, setAddVisitForm] = useState({
    date: "",
    time: "",
    note: "",
    user: "",
    lead: "",
  });
  const [userOptions, setUserOptions] = useState([]);
  const [leadOptions, setLeadOptions] = useState([]);
  const navigate = useNavigate();
  const actionMenuRef = useRef(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);

  // Get user from localStorage or your app's context/redux
  const [user, setUser] = useState(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) setUser(JSON.parse(stored));
    } catch (e) {}
  }, []);

  const businessId = localStorage.getItem("businessId") || "";

  // Permission checks
  const canListVisit = hasPermission(user, "Lead Visit Settings", "List");
  const canViewVisit = hasPermission(user, "Lead Visit Settings", "View");
  const canCreateVisit = hasPermission(user, "Lead Visit Settings", "Create");
  const canEditVisit = hasPermission(user, "Lead Visit Settings", "Edit");
  const canDeleteVisit = hasPermission(user, "Lead Visit Settings", "Delete");
  const canRemarkVisit = hasPermission(user, "Lead Visit Settings", "Remark");

  const handleActionMenuClick = (e, id) => {
    e.stopPropagation();
    setActionMenuOpen((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setActionMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDelete = async (row) => {
    if (!canDeleteVisit) {
      alert("No permission to delete visits.");
      return;
    }
    // Confirm delete using SweetAlert helper
    try {
      const res = await confirmDelete('Are you sure you want to delete this visit?');
      if (!res.isConfirmed) return;
    } catch (err) {
      return;
    }
    try {
      await axios.delete(`/admin/api/lead-visit/${row._id}`);
      fetchVisits();
      setActionMenuOpen(null);
    } catch (err) {
      alert("Failed to delete visit");
    }
  };

  const handleDeleteImage = async (imageUrl, imageIndex) => {
    if (!canEditVisit) {
      alert("No permission to delete images.");
      return;
    }
    try {
      const res = await axios.delete(`/admin/api/lead-visit/${remarkRow._id}/image`, {
        data: { imageUrl: imageUrl.replace(IMAGE_BASE_URL, '') }
      });
      if (res.data && res.data.Status === 1) {
        setExistingImages(prev => prev.filter((_, idx) => idx !== imageIndex));
        alert("Image deleted successfully");
        fetchVisits();
      } else {
        alert(res.data.Message || "Failed to delete image");
      }
    } catch {
      alert("Failed to delete image");
    }
  };

  const handleViewImage = (imageIndex) => {
    setSelectedImageIndex(imageIndex);
    setShowImageModal(true);
  };

  useEffect(() => {
    fetchVisits();
  }, [canListVisit, businessId]);

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await axios.get(`/api/user`, {
          params: { business: businessId }
        });
        if (res.data && Array.isArray(res.data.users)) {
          setUserOptions(
            res.data.users.map(u => ({
              value: u._id,
              label: `${u.firstName || ""} ${u.lastName || ""}`.trim()
            }))
          );
        }
      } catch {
        setUserOptions([]);
      }
    }
    async function fetchLeads() {
      try {
        const res = await axios.get(`/admin/api/lead`, {
          params: { business: businessId }
        });
        if (res.data && Array.isArray(res.data.leads)) {
          setLeadOptions(
            res.data.leads.map(l => ({
              value: l._id,
              label: l.name || l.leadNumber || ""
            }))
          );
        }
      } catch {
        setLeadOptions([]);
      }
    }
    if (businessId) {
      fetchUsers();
      fetchLeads();
    }
  }, [businessId]);

  const fetchVisits = async () => {
    if (!canListVisit && !canViewVisit) {
      setVisits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { business: businessId };
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const res = await axios.get("/admin/api/lead-visit", { params });
      setVisits(res.data.visits || []);
    } catch {
      setVisits([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchVisits();
  }, [filters, businessId, canListVisit, canViewVisit]);

  const handleAddVisitSubmit = async () => {
    if (!canCreateVisit) {
      alert("No permission to create a new visit.");
      return;
    }
    try {
      let visitAt = addVisitForm.date;
      if (addVisitForm.time) visitAt += " " + addVisitForm.time;
      const payload = {
        visitAt,
        note: addVisitForm.note,
        user: addVisitForm.user,
        lead: addVisitForm.lead,
        business: businessId,
      };
      const res = await axios.post("/admin/api/lead-visit", payload);
      if (res.data && res.data.Status === 1) {
        alert("Lead Visit added Successfully");
        setShowVisitModal(false);
        setAddVisitForm({ date: "", time: "", note: "", user: "", lead: "" });
        fetchVisits();
      } else {
        alert(res.data.Message || "Failed to add lead visit");
      }
    } catch {
      alert("Failed to add lead visit");
    }
  };

  const handleRemarkSubmit = async () => {
    if (!canRemarkVisit) {
      alert("No permission to upload remarks.");
      return;
    }
    try {
      if (!remarkRow || !remarkRow._id) {
        alert("No visit selected for remark");
        return;
      }
      if (!remarkFormValues.remark.trim()) {
        alert("Please enter a remark before uploading");
        return;
      }
      if (remarkFormValues.image && remarkFormValues.image.length > 0) {
        const files = Array.isArray(remarkFormValues.image) ? remarkFormValues.image : [remarkFormValues.image];
        const totalFiles = existingImages.length + files.length;
        if (totalFiles > 6) {
          alert(`Maximum 6 files allowed. You currently have ${existingImages.length} existing files.`);
          return;
        }
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        const maxSize = 2 * 1024 * 1024;
        for (let file of files) {
          if (!allowedTypes.includes(file.type) || file.size > maxSize) {
            alert(`Check file type/size for ${file.name}. Only JPG, JPEG, PNG, PDF under 2MB allowed.`);
            return;
          }
        }
      }
      setIsUploading(true);
      setUploadProgress(0);
      const formData = new FormData();
      formData.append("remark", remarkFormValues.remark);
      if (remarkFormValues.image)
        (Array.isArray(remarkFormValues.image) ? remarkFormValues.image : [remarkFormValues.image])
          .forEach(img => formData.append("images", img));
      const res = await axios.put(`/admin/api/lead-visit/${remarkRow._id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (pe) => {
          const percentCompleted = Math.round((pe.loaded * 100) / pe.total);
          setUploadProgress(percentCompleted);
        }
      });
      if (res.data && res.data.Status === 1) {
        alert("Remark uploaded successfully");
        setShowRemarkModal(false);
        setRemarkRow(null);
        setRemarkFormValues({ remark: "", image: null });
        setExistingImages([]);
        setUploadProgress(0);
        setIsUploading(false);
        fetchVisits();
      } else {
        alert(res.data.Message || "Failed to upload remark");
        setIsUploading(false);
        setUploadProgress(0);
      }
    } catch {
      alert("Failed to upload remark");
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const fetchRemarkData = async (visitId) => {
    try {
      const res = await axios.get(`/admin/api/lead-visit/${visitId}`);
      if (res.data && res.data.Status === 1) {
        const visitData = res.data.visit;
        setRemarkFormValues({
          remark: visitData.remark || "",
          image: null
        });
        setExistingImages(
          visitData.images ? visitData.images.map(image => `${IMAGE_BASE_URL}${image}`) : []
        );
      }
    } catch {
      setRemarkFormValues({ remark: "", image: null });
      setExistingImages([]);
    }
  };

  const tableData = visits.map((row, idx) => ({
    srNo: idx + 1,
    leadName: typeof row.lead === "object"
      ? row.lead.name || row.lead.leadNumber || ""
      : row.lead,
    userName: (row.selecteduser && typeof row.selecteduser === 'object')
      ? `${row.selecteduser.firstName || ""} ${row.selecteduser.lastName || ""}`.trim()
      : (typeof row.user === "object"
        ? `${row.user.firstName || ""} ${row.user.lastName || ""}`.trim()
        : row.user),
    note: row.note,
    createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString() : "",
    action: (
      <div className="d-flex gap-2 align-items-center" style={{ position: "relative" }}>
        {canRemarkVisit && (
          <FaRegCommentDots
            title="Add Remark"
            onClick={async () => {
              setRemarkRow(row);
              setShowRemarkModal(true);
              await fetchRemarkData(row._id);
            }}
            style={{ cursor: "pointer", fontSize: "16px", color: "#3F3F46" }}
          />
        )}
        <FaEllipsisV
          title="Actions"
          onClick={(e) => handleActionMenuClick(e, row._id)}
          style={{
            cursor: "pointer",
            fontSize: "16px",
            color: "#3F3F46"
          }}
        />
        {actionMenuOpen === row._id && (
          <div
            ref={actionMenuRef}
            style={{
              position: "fixed",
              zIndex: 1050,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 6,
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
              minWidth: 150,
              maxWidth: 170,
              padding: "6px 0",
            }}
          >
            {canViewVisit && (
              <button
                className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-primary-custom d-flex align-items-center"
                onClick={() => navigate(`/user/visit/view/${row._id}`)}
                style={{ gap: "6px" }}
              >
                <FaEye size={14} /> View
              </button>
            )}
            {canDeleteVisit && (
              <button
                className="btn btn-sm w-100 text-start py-1 px-2 lh-sm shadow-none border-0 text-danger d-flex align-items-center"
                onClick={() => handleDelete(row)}
                style={{ gap: "6px" }}
              >
                <FaTrashAlt size={14} /> Delete
              </button>
            )}
          </div>
        )}
      </div>
    ),
  }));

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 mt-3">
        <h2 className="fw-bold mb-0" style={{ fontSize: "1.75rem" }}>
          Leads Visits
        </h2>
        <div>
          <button
            className={`btn btn-light me-2 ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <FaFilter className="me-1" /> Filter
          </button>
          {canCreateVisit && (
            <button
              className="btn btn-primary px-3 rounded-3"
              onClick={() => setShowVisitModal(true)}
            >
              <FaPlus className="me-1" /> Add New Visit
            </button>
          )}
        </div>
      </div>
      <div className="d-flex justify-content-end mb-3">
        <div
          className="input-group"
          style={{ width: 220, borderRadius: '6px', overflow: 'hidden' }}
        >
          <span
            className="input-group-text bg-white border-end-0"
            style={{ borderRadius: '6px 0 0 6px' }}
          >
            <i className="fas fa-search text-muted"></i>
          </span>
          <input
            className="form-control border-start-0"
            type="search"
            placeholder="Type to search"
            style={{ boxShadow: 'none', borderRadius: '0 6px 6px 0' }}
          />
        </div>
      </div>
      {showFilters && (
        <CommanFilters
          filters={filters}
          onFilterChange={(key, value) => setFilters(f => ({ ...f, [key]: value }))}
          fields={["dateRange"]}
          onApplyFilters={() => {}}
          onClearFilters={() => setFilters({ startDate: "", endDate: "" })}
        />
      )}
      <CommonTable
        columns={leadsVisitsColumns}
        data={tableData}
        defaultItemsPerPage={15}
        showPagination={true}
        loading={loading}
      />
      {showVisitModal && (
        <CommonModalForm
          visible={showVisitModal}
          title="Leads Visits"
          onCancel={() => setShowVisitModal(false)}
          onSubmit={handleAddVisitSubmit}
          values={addVisitForm}
          onChange={setAddVisitForm}
          fields={[
            {
              name: "date",
              label: "Select Date",
              type: "date",
              placeholder: "Select date"
            },
            {
              name: "time",
              label: "Select Time",
              type: "time",
              placeholder: "Select time"
            },
            {
              name: "lead",
              label: "Lead Name",
              type: "select",
              options: [{ value: "", label: "Select Lead" }, ...leadOptions],
              placeholder: "Select Lead"
            },
            {
              name: "user",
              label: "Select User",
              type: "select",
              options: [{ value: "", label: "Select User" }, ...userOptions],
              placeholder: "Select User"
            },
            {
              name: "note",
              label: "Note",
              type: "textarea",
              placeholder: "Note",
              rows: 5
            }
          ]}
        />
      )}
      {showRemarkModal && (
        <div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={() => {
              if (!isUploading) {
                setShowRemarkModal(false);
                setRemarkRow(null);
                setRemarkFormValues({ remark: "", image: null });
                setExistingImages([]);
                setUploadProgress(0);
              }
            }}
          >
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content rounded-3">
                <div className="modal-header">
                  <h5 className="modal-title">Upload Remark & Attachments</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      if (!isUploading) {
                        setShowRemarkModal(false);
                        setRemarkRow(null);
                        setRemarkFormValues({ remark: "", image: null });
                        setExistingImages([]);
                        setUploadProgress(0);
                      }
                    }}
                    disabled={isUploading}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">
                      Remark <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Enter your remark here..."
                      value={remarkFormValues.remark}
                      onChange={(e) => setRemarkFormValues(prev => ({ ...prev, remark: e.target.value }))}
                      disabled={isUploading}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="attachments" className="form-label">
                      Other Attachments (Max 6)
                      <span className="text-danger">*</span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm rounded-pill px-2 py-0 ms-2"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="You may upload up to 6 files. Allowed formats: jpg, jpeg, png, pdf Max 2MB each."
                        style={{ fontSize: '12px', lineHeight: '1' }}
                      >
                        ?
                      </button>
                    </label>
                    <input
                      id="attachments"
                      className="form-control"
                      type="file"
                      name="attachments[]"
                      multiple
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => setRemarkFormValues(prev => ({ ...prev, image: Array.from(e.target.files) }))}
                      disabled={isUploading}
                    />
                    <div className="form-text text-muted">
                      Select up to 6 files (JPG, JPEG, PNG, PDF). Maximum 2MB per file.
                    </div>
                  </div>
                  {remarkFormValues.image && remarkFormValues.image.length > 0 && (
                    <div className="mb-3">
                      <h6>Selected Files ({remarkFormValues.image.length}/6):</h6>
                      <div className="list-group">
                        {remarkFormValues.image.map((file, idx) => (
                          <div key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                              <i className={`fas ${file.type.includes('pdf') ? 'fa-file-pdf text-danger' : 'fa-image text-primary'} me-2`}></i>
                              <span className="me-2">{file.name}</span>
                              <small className="text-muted">({(file.size / 1024 / 1024).toFixed(2)} MB)</small>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                const newFiles = remarkFormValues.image.filter((_, i) => i !== idx);
                                setRemarkFormValues(prev => ({ ...prev, image: newFiles }));
                              }}
                              disabled={isUploading}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      if (!isUploading) {
                        setShowRemarkModal(false);
                        setRemarkRow(null);
                        setRemarkFormValues({ remark: "", image: null });
                        setExistingImages([]);
                        setUploadProgress(0);
                      }
                    }}
                    disabled={isUploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleRemarkSubmit}
                    enable={isUploading || !(remarkFormValues.remark?.trim() || (remarkFormValues.image && remarkFormValues.image.length > 0))}
                  >
                    {isUploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Uploading...
                      </>
                    ) : (
                      'Upload Remark'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {isUploading && (
            <div style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              padding: '20px',
              border: '1px solid #ccc',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              zIndex: 10000,
              minWidth: '300px'
            }}>
              <div className="text-center mb-3">
                <strong>Uploading... {uploadProgress}%</strong>
              </div>
              <div className="progress" style={{ height: '10px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
            </div>
          )}
          {existingImages.length > 0 && (
            <div style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              background: 'white',
              padding: '20px',
              border: '1px solid #ccc',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              maxWidth: '350px',
              zIndex: 9999,
              maxHeight: '70vh',
              overflowY: 'auto'
            }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-bold">Existing Images ({existingImages.length})</h6>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setExistingImages([])}
                  title="Close"
                >
                  ×
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '12px' }}>
                {existingImages.map((imageUrl, idx) => (
                  <div key={idx} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
                    <img
                      src={imageUrl}
                      alt={`Existing ${idx + 1}`}
                      style={{
                        width: '100%',
                        height: '80px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleViewImage(idx)}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.borderColor = '#007bff';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.borderColor = '#e9ecef';
                      }}
                    />
                    <button
                      className="btn btn-danger btn-sm"
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        width: '20px',
                        height: '20px',
                        padding: '0',
                        fontSize: '10px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(imageUrl, idx);
                      }}
                      title="Delete image"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="text-muted small mt-2 text-center">
                Click image to view • Click × to delete
              </div>
            </div>
          )}
        </div>
      )}

      {showImageModal && existingImages.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowImageModal(false)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                fontSize: '18px',
                cursor: 'pointer',
                zIndex: 10001
              }}
              onClick={() => setShowImageModal(false)}
            >
              ×
            </button>
            {existingImages.length > 1 && (
              <button
                style={{
                  position: 'absolute',
                  left: '-50px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  zIndex: 10001
                }}
                onClick={() => setSelectedImageIndex((prev) =>
                  prev === 0 ? existingImages.length - 1 : prev - 1
                )}
              >
                ‹
              </button>
            )}

            <img
              src={existingImages[selectedImageIndex]}
              alt={`Image ${selectedImageIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.5)'
              }}
            />

            {existingImages.length > 1 && (
              <button
                style={{
                  position: 'absolute',
                  right: '-50px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  zIndex: 10001
                }}
                onClick={() => setSelectedImageIndex((prev) =>
                  prev === existingImages.length - 1 ? 0 : prev + 1
                )}
              >
                ›
              </button>
            )}

            {existingImages.length > 1 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '-40px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  padding: '5px 15px',
                  borderRadius: '15px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {selectedImageIndex + 1} / {existingImages.length}
              </div>
            )}

            <button
              style={{
                position: 'absolute',
                top: '-40px',
                left: '0',
                background: 'rgba(220, 53, 69, 0.9)',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                padding: '8px 15px',
                fontSize: '12px',
                cursor: 'pointer',
                zIndex: 10001
              }}
              onClick={() => {
                handleDeleteImage(existingImages[selectedImageIndex], selectedImageIndex);
                if (existingImages.length === 1) {
                  setShowImageModal(false);
                } else {
                  if (selectedImageIndex >= existingImages.length - 1) {
                    setSelectedImageIndex(0);
                  }
                }
              }}
            >
              🗑 Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitIndex;